import type { LetterFormValues, GeneratedElfLetter } from "../types/elf-letter.ts";

export const emptyElfLetterDraft: LetterFormValues = {
  childName: "",
  elfName: "",
  specialInstructions: "",
};

export function normalizeElfLetterDraft(value: Partial<LetterFormValues>): LetterFormValues {
  const normalize = (text: unknown) => typeof text === "string" ? text.trim() : "";
  return {
    childName: normalize(value.childName),
    elfName: normalize(value.elfName),
    specialInstructions: normalize(value.specialInstructions),
  };
}

/** Tolerate v1 sessions predating optional details; reject non-string form values. */
export function recoverElfLetterDraft(value: unknown): LetterFormValues {
  if (!value || typeof value !== "object") return { ...emptyElfLetterDraft };
  const saved = value as Record<string, unknown>;
  return {
    childName: typeof saved.childName === "string" ? saved.childName : "",
    elfName: typeof saved.elfName === "string" ? saved.elfName : "",
    specialInstructions: typeof saved.specialInstructions === "string" ? saved.specialInstructions : "",
  };
}

export function elfLetterDraftMatches(
  form: LetterFormValues,
  letter: Partial<LetterFormValues> | null | undefined,
): boolean {
  if (!letter) return false;
  const current = normalizeElfLetterDraft(form);
  const previous = normalizeElfLetterDraft(letter);
  return current.childName === previous.childName
    && current.elfName === previous.elfName
    && current.specialInstructions === previous.specialInstructions;
}

export function hasElfLetterDraftDetails(form: LetterFormValues): boolean {
  return Object.values(normalizeElfLetterDraft(form)).some(Boolean);
}

export function canDeliverElfLetter(
  form: LetterFormValues,
  active: GeneratedElfLetter | undefined,
  delivery: GeneratedElfLetter | null,
  generating: boolean,
): boolean {
  return !generating && Boolean(
    active && delivery
    && active.letterId === delivery.letterId
    && elfLetterDraftMatches(form, delivery),
  );
}

/** Any recovered family detail requires review before a URL can apply a starter. */
export function requiresOccasionReview(
  form: LetterFormValues,
  previousStarter: string | undefined,
  fromDeepLink: boolean,
): boolean {
  if (fromDeepLink) return hasElfLetterDraftDetails(form);
  const details = normalizeElfLetterDraft(form).specialInstructions;
  return Boolean(details && details !== normalizeElfLetterDraft({ specialInstructions: previousStarter }).specialInstructions);
}
